import type { WrappedResponse } from '../../api/types';
import { LINES, lineForGenre, type Line } from '../lines';
import { graderWord, int, share, topPercent } from './format';

/** The top pick the ticket names as the next stop. */
export interface NextPick {
  id: number;
  title: string;
}

/** Everything the printed stub says, resolved once so the DOM ticket and the PNG cannot disagree. */
export interface StubFacts {
  rider: string;
  source: string;
  line: Line | null;
  genre: string | null;
  genreShare: string | null;
  titles: string;
  hours: string;
  /** "MORE OFFBEAT THAN 71%" (or MAINSTREAM, whichever the list is), or null. */
  obscurity: string | null;
  grader: string | null;
  gem: string | null;
  dropRate: string | null;
  /** The rider's lines in riding order, as the route strip prints them. */
  network: { code: string; color: string }[];
  /** The title at the top of the rider's recommendations, if there is one. */
  next: string | null;
  /** Today, as the stamp dates it: 2026.10.27. */
  issued: string;
  /** Decorative: the same rider always prints the same bars. */
  bars: number[];
  serial: string;
}

function hash(text: string): number {
  let h = 2166136261;
  for (const c of text) h = Math.imul(h ^ c.charCodeAt(0), 16777619);
  return h >>> 0;
}

function today(): string {
  const d = new Date();
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * `boarding` is the line the ticket is for: the first on the rider's network,
 * where the train goes next. Without one, the home genre's line. `network` is
 * every line the rider rides, in order, and `next` their first pick.
 */
export function stubFacts(
  data: WrappedResponse,
  boarding: Line | null = null,
  network: Line[] = [],
  next: string | null = null,
): StubFacts {
  const genre = data.home?.genre ?? null;
  const lineSlug = genre ? lineForGenre(genre) : null;
  const line = boarding ?? (lineSlug ? (LINES.find((l) => l.slug === lineSlug) ?? null) : null);
  const seed = hash(`${data.profile.source}:${data.profile.username}`);
  const bars: number[] = [];
  for (let i = 0, h = seed; i < 34; i++) {
    h = Math.imul(h ^ (h >>> 15), 2246822507) >>> 0;
    bars.push(1 + (h % 4));
  }
  return {
    rider: data.profile.username,
    source: data.profile.source === 'mal' ? 'MYANIMELIST' : 'ANILIST',
    line,
    genre,
    genreShare: data.home ? share(data.home.share) : null,
    titles: int(data.departure.titles),
    hours: int(data.departure.hours),
    obscurity: data.obscurity
      ? data.obscurity.percentile >= 50
        ? `MORE OFFBEAT THAN ${topPercent(data.obscurity.percentile)}%`
        : `MORE MAINSTREAM THAN ${topPercent(100 - data.obscurity.percentile)}%`
      : null,
    grader: data.harshness ? graderWord(data.harshness.delta) : null,
    gem: data.hidden_gem?.title.title ?? null,
    dropRate: data.drops ? share(data.drops.rate) : null,
    // Before the picks are in there is no network yet: the line the ticket is for stands alone.
    network: (network.length > 0 ? network : line ? [line] : []).map((l) => ({ code: l.code, color: l.color })),
    next,
    issued: today(),
    bars,
    serial: String(seed % 1_000_000).padStart(6, '0'),
  };
}

/** graderWord's answers, harshest first: the share link sends the index. */
const GRADES = ['A TOUGH GRADER', 'A LITTLE STRICT', 'RIGHT ON THE CROWD', 'A LITTLE GENEROUS', 'A GENEROUS GRADER'];

/**
 * The ticket as the share link carries it, for the card the backend draws
 * (backend/app/card.py). No username: the card is the rider's numbers only.
 * Values go over already rounded the way the ticket prints them, so the card
 * cannot come out a percent different.
 */
export function shareQuery(data: WrappedResponse, f: StubFacts, network: Line[], nextId: number | null): string {
  const q = new URLSearchParams();
  const put = (key: string, value: string | number | null | undefined) => {
    if (value !== null && value !== undefined && value !== '') q.set(key, String(value));
  };
  put('l', f.line?.slug);
  put('n', network.map((l) => l.slug).join('.'));
  put('t', Math.round(data.departure.titles));
  put('h', Math.round(data.departure.hours));
  if (data.home) {
    put('g', data.home.genre);
    put('gs', Math.round(data.home.share * 1000));
  }
  if (data.obscurity) {
    const p = data.obscurity.percentile;
    put('o', p >= 50 ? topPercent(p) : -topPercent(100 - p));
  }
  const grade = f.grader ? GRADES.indexOf(f.grader) : -1;
  put('gr', grade >= 0 ? grade : null);
  put('gem', data.hidden_gem?.media_id);
  put('nx', nextId);
  if (data.drops) put('dr', Math.round(data.drops.rate * 1000));
  put('s', Number(f.serial));
  put('d', f.issued.replaceAll('.', ''));
  return q.toString();
}
